/**
 * enrichJornadaDemoVentas.ts — Descuentos, propina y multi-ítem en ventas demo jornada.
 *
 * Enriquece cada slot del turno (0–5) con un perfil distinto para historial/cierre.
 * Usa productos reales del catálogo inventario (demoInventoryProductMap).
 */

import { defaultProducts } from "@/mocks/data/products";
import type { Venta, VentaProducto } from "../types/caja.types";
import { buildServicioPorMetodo } from "../utils/propinaAllocation";
import { getDemoProductLineForPunto } from "./demoInventoryProductMap";

const money = (n: number) => Math.max(0, Math.round(n));

type DemoPricingProfile =
  | { kind: "plain"; lineCount: number }
  | { kind: "itemDisc"; lineCount: number; itemPct: number }
  | { kind: "globalDisc"; lineCount: number; globalPct: number }
  | { kind: "propina"; lineCount: number; servicioPct: number }
  | {
      kind: "itemGlobal";
      lineCount: number;
      itemPct: number;
      globalPct: number;
    }
  | {
      kind: "combo";
      lineCount: number;
      itemPct: number;
      globalPct: number;
      servicioPct: number;
    };

/** Perfil por posición del turno (6 ventas = 6 escenarios distintos). */
const SLOT_PROFILES: DemoPricingProfile[] = [
  { kind: "plain", lineCount: 3 },
  { kind: "itemDisc", lineCount: 1, itemPct: 5 },
  { kind: "globalDisc", lineCount: 2, globalPct: 15 },
  { kind: "propina", lineCount: 1, servicioPct: 20 },
  { kind: "itemGlobal", lineCount: 3, itemPct: 5, globalPct: 10 },
  { kind: "combo", lineCount: 4, itemPct: 5, globalPct: 10, servicioPct: 15 },
];

function catalogUnitPrice(productoId: string): number {
  const hit = defaultProducts.find((p) => String(p.id) === productoId);
  const raw = hit?.precio ?? 50_000;
  return money(Number(raw) || 50_000);
}

function buildProductLines(
  puntoId: string,
  slotIndex: number,
  count: number,
): VentaProducto[] {
  const lines: VentaProducto[] = [];
  for (let i = 0; i < count; i++) {
    const cat = getDemoProductLineForPunto(puntoId, slotIndex + i);
    const precioUnitario = catalogUnitPrice(cat.productoId);
    lines.push({
      productoId: cat.productoId,
      nombre: cat.nombre,
      cantidad: 1,
      precioUnitario,
    });
  }
  return lines;
}

function scaleMixtoBreakdown(
  venta: Venta,
  prevTotal: number,
  nextTotal: number,
): Venta["detalleMetodoPago"] {
  const breakdown = venta.detalleMetodoPago?.breakdown;
  if (!breakdown?.length || prevTotal <= 0) return venta.detalleMetodoPago;

  let allocated = 0;
  const scaled = breakdown.map((b, i) => {
    const isLast = i === breakdown.length - 1;
    const amount = isLast
      ? nextTotal - allocated
      : Math.round((b.amount * nextTotal) / prevTotal);
    if (!isLast) allocated += amount;
    return { ...b, amount };
  });
  return { breakdown: scaled };
}

interface LineCalc {
  lineId: string;
  nombre: string;
  bruto: number;
  descuentoItem: number;
  netoAntesGlobal: number;
  netoItem: number;
  descuentoGlobalAsignado: number;
  descuentoTotalLinea: number;
  precioUnitarioOriginal: number;
  precioUnitarioFinal: number;
  cantidad: number;
  ajusteAplicado: {
    mode: "discountPercent";
    discountPercent: number;
  } | null;
}

function buildPricing(
  productos: VentaProducto[],
  profile: DemoPricingProfile,
): {
  productos: VentaProducto[];
  subtotalBruto: number;
  totalDescuentoItems: number;
  descuentoGlobal: number;
  subtotal: number;
  servicioPct: number;
  servicioAmount: number;
  total: number;
  descuentos?: Venta["descuentos"];
} {
  const itemPct =
    profile.kind === "itemDisc" ||
    profile.kind === "itemGlobal" ||
    profile.kind === "combo"
      ? profile.itemPct
      : 0;
  const globalPct =
    profile.kind === "globalDisc" ||
    profile.kind === "itemGlobal" ||
    profile.kind === "combo"
      ? profile.globalPct
      : 0;
  const servicioPct =
    profile.kind === "propina"
      ? profile.servicioPct
      : profile.kind === "combo"
        ? profile.servicioPct
        : 0;

  const lineCalcs: LineCalc[] = productos.map((p, idx) => {
    const bruto = money(p.cantidad * p.precioUnitario);
    const applyItemDisc =
      itemPct > 0 &&
      (profile.kind === "itemDisc" ||
        ((profile.kind === "itemGlobal" || profile.kind === "combo") &&
          idx === 0));
    const descuentoItem =
      applyItemDisc && itemPct > 0 ? money((bruto * itemPct) / 100) : 0;
    const netoAntesGlobal = bruto - descuentoItem;
    return {
      lineId: `${p.productoId}#${idx}`,
      nombre: p.nombre,
      bruto,
      descuentoItem,
      netoAntesGlobal,
      netoItem: netoAntesGlobal,
      descuentoGlobalAsignado: 0,
      descuentoTotalLinea: descuentoItem,
      precioUnitarioOriginal: p.precioUnitario,
      precioUnitarioFinal:
        p.cantidad > 0 ? money(netoAntesGlobal / p.cantidad) : p.precioUnitario,
      cantidad: p.cantidad,
      ajusteAplicado:
        descuentoItem > 0
          ? { mode: "discountPercent", discountPercent: itemPct }
          : null,
    };
  });

  const subtotalBruto = lineCalcs.reduce((s, l) => s + l.bruto, 0);
  const totalDescuentoItems = lineCalcs.reduce(
    (s, l) => s + l.descuentoItem,
    0,
  );
  const subtotalNetoItems = lineCalcs.reduce(
    (s, l) => s + l.netoAntesGlobal,
    0,
  );

  let descuentoGlobal = 0;
  if (globalPct > 0 && subtotalNetoItems > 0) {
    descuentoGlobal = money((subtotalNetoItems * globalPct) / 100);
    let assigned = 0;
    lineCalcs.forEach((line, idx) => {
      const isLast = idx === lineCalcs.length - 1;
      const share = isLast
        ? descuentoGlobal - assigned
        : money((line.netoAntesGlobal / subtotalNetoItems) * descuentoGlobal);
      if (!isLast) assigned += share;
      line.descuentoGlobalAsignado = Math.max(0, share);
      line.descuentoTotalLinea =
        line.descuentoItem + line.descuentoGlobalAsignado;
      line.netoItem = Math.max(
        0,
        line.netoAntesGlobal - line.descuentoGlobalAsignado,
      );
      line.precioUnitarioFinal =
        line.cantidad > 0
          ? money(line.netoItem / line.cantidad)
          : line.precioUnitarioOriginal;
    });
  }

  const subtotal = lineCalcs.reduce((s, l) => s + l.netoItem, 0);
  const servicioAmount =
    servicioPct > 0 ? money((subtotal * servicioPct) / 100) : 0;
  const total = subtotal + servicioAmount;

  const pricedProductos = productos.map((p, idx) => ({
    ...p,
    precioUnitario: lineCalcs[idx]?.precioUnitarioFinal ?? p.precioUnitario,
  }));

  const hasDiscount = totalDescuentoItems > 0 || descuentoGlobal > 0;

  const descuentos = hasDiscount
    ? {
        totalDescuentoItems,
        descuentoGlobal,
        globalDiscountPct: globalPct > 0 ? globalPct : undefined,
        lineAdjustments: lineCalcs
          .filter((l) => l.descuentoTotalLinea > 0)
          .map((l) => ({
            nombre: l.nombre,
            descuentoTotalLinea: l.descuentoTotalLinea,
          })),
        lineDetails: lineCalcs.map((l, idx) => ({
          productoId: productos[idx]?.productoId ?? l.lineId,
          nombre: l.nombre,
          bruto: l.bruto,
          netoFinal: l.netoItem,
          cantidad: l.cantidad,
          precioUnitarioFinal: l.precioUnitarioFinal,
          precioUnitarioOriginal: l.precioUnitarioOriginal,
        })),
        breakdownSnapshot: {
          subtotalBruto,
          totalDescuentoItems,
          descuentoGlobal,
          globalDiscount:
            globalPct > 0 ? { type: "%" as const, value: globalPct } : null,
          lines: lineCalcs.map((l) => ({
            lineId: l.lineId,
            nombre: l.nombre,
            bruto: l.bruto,
            netoAntesGlobal: l.netoAntesGlobal,
            descuentoItem: l.descuentoItem,
            netoItem: l.netoItem,
            descuentoGlobalAsignado: l.descuentoGlobalAsignado,
            descuentoTotalLinea: l.descuentoTotalLinea,
            precioUnitarioOriginal: l.precioUnitarioOriginal,
            precioUnitarioFinal: l.precioUnitarioFinal,
            cantidad: l.cantidad,
            ajusteAplicado: l.ajusteAplicado,
          })),
        },
      }
    : undefined;

  return {
    productos: pricedProductos,
    subtotalBruto,
    totalDescuentoItems,
    descuentoGlobal,
    subtotal,
    servicioPct,
    servicioAmount,
    total,
    descuentos,
  };
}

/** Aplica perfil de precios demo según slot del turno (0–5). */
export function enrichJornadaDemoVenta(venta: Venta, slotIndex: number): Venta {
  if (venta.estado === "anulada") return venta;

  const profile = SLOT_PROFILES[slotIndex % SLOT_PROFILES.length];
  const puntoId = venta.puntoCajaId ?? "pc1";
  const productos = buildProductLines(puntoId, slotIndex, profile.lineCount);
  const prevTotal = venta.total;
  const pricing = buildPricing(productos, profile);

  const detalleMetodoPago =
    venta.metodoPago === "mixto"
      ? scaleMixtoBreakdown(venta, prevTotal, pricing.total)
      : venta.detalleMetodoPago;

  const servicioPorMetodo =
    pricing.servicioAmount > 0
      ? buildServicioPorMetodo({
          total: pricing.total,
          servicioAmount: pricing.servicioAmount,
          metodoPago: venta.metodoPago,
          breakdown:
            venta.metodoPago === "mixto"
              ? detalleMetodoPago?.breakdown
              : [{ method: venta.metodoPago, amount: pricing.total }],
        })
      : undefined;

  return {
    ...venta,
    productos: pricing.productos,
    subtotal: pricing.subtotal,
    servicioPct: pricing.servicioPct,
    servicioAmount: pricing.servicioAmount,
    servicioPorMetodo,
    total: pricing.total,
    descuentos: pricing.descuentos,
    detalleMetodoPago,
  };
}

export function enrichJornadaDemoVentas(ventas: Venta[]): Venta[] {
  return ventas.map((v) => {
    const slotMatch = v.id.match(/-(\d+)$/);
    const slotIndex = slotMatch
      ? Math.max(0, parseInt(slotMatch[1], 10) - 1)
      : 0;
    return enrichJornadaDemoVenta(v, slotIndex);
  });
}
